import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Mail, Phone, MapPin } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import type { VillageChief } from "@shared/schema";

interface VillageChiefSectionProps {
  chief?: VillageChief;
  isLoading: boolean;
}

export function VillageChiefSection({ chief, isLoading }: VillageChiefSectionProps) {
  const [, setLocation] = useLocation();

  if (isLoading) {
    return (
      <section className="py-12" data-testid="section-village-chief">
        <div className="text-center mb-8">
          <Skeleton className="h-10 w-64 mx-auto mb-2 rounded-xl neo-card-sm" />
          <Skeleton className="h-4 w-96 mx-auto rounded-xl neo-card-sm" />
        </div>
        <div className="p-8 rounded-2xl bg-card neo-card">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <Skeleton className="w-32 h-32 rounded-full neo-avatar" />
            <div className="flex-1 space-y-4 w-full">
              <Skeleton className="h-8 w-48 rounded-xl neo-card-sm" />
              <Skeleton className="h-6 w-32 rounded-xl neo-card-sm" />
              <Skeleton className="h-24 w-full rounded-xl neo-card-sm" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-full max-w-md rounded-xl neo-card-sm" />
                <Skeleton className="h-5 w-full max-w-md rounded-xl neo-card-sm" />
                <Skeleton className="h-5 w-full max-w-md rounded-xl neo-card-sm" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!chief) {
    return null;
  }

  const initials = chief.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  return (
    <section className="py-12" data-testid="section-village-chief">
      <div className="text-center mb-8">
        <h2 className="font-poppins text-3xl md:text-4xl font-bold text-foreground mb-2" data-testid="heading-village-chief">
          Village Leadership
        </h2>
        <p className="font-inter text-muted-foreground text-lg" data-testid="text-chief-subtitle">
          Serving our community with dedication and commitment
        </p>
      </div>

      <div className="p-8 rounded-2xl bg-card neo-card transition-all duration-300" data-testid="card-village-chief">
        <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
          <Avatar className="w-32 h-32 neo-avatar border-none" data-testid="avatar-chief">
            <AvatarImage src={chief.photo} alt={chief.name} />
            <AvatarFallback className="bg-primary text-primary-foreground text-3xl font-poppins">
              {initials}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 text-center md:text-left">
            <h3 className="font-poppins text-2xl md:text-3xl font-bold text-foreground mb-1" data-testid="text-chief-name">
              {chief.name}
            </h3>
            <p className="font-inter text-lg text-primary font-semibold mb-4" data-testid="text-chief-title">
              {chief.title}
            </p>

            <p className="font-inter text-foreground leading-relaxed mb-6" data-testid="text-chief-message">
              {chief.message}
            </p>

            <div className="space-y-3">
              <div className="flex items-center gap-3 text-foreground" data-testid="contact-chief-phone">
                <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="font-inter">{chief.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-foreground" data-testid="contact-chief-email">
                <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="font-inter">{chief.email}</span>
              </div>
              <div className="flex items-center gap-3 text-foreground" data-testid="contact-chief-address">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="font-inter">{chief.address}</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
